// ============================================
// PESTANACOMPRAS.JS — Compras al almacen (Parte 2, 2026-10-17; rearmado 2026-10-19)
// ============================================
// Solo la ven direccion y compras (permiso almacen.compras). Correccion de Ricardo del 19-oct:
//   - La compra YA NO asigna a proyectos: al confirmar, TODO entra al almacen a su costo real (promedio
//     ponderado, con el flete repartido). De ahi cada expediente "trae del almacen" lo suyo.
//   - Hoja de compras: antes de comprar, mira lo que piden los proyectos contra lo que HAY en el almacen y
//     marca con senales claras lo que ya esta; solo lista el faltante real.
//   - Traer de Syscom (API de facturas) sigue siendo el camino principal; el XML es el respaldo; otros
//     proveedores a mano con foto del comprobante.
// Celular primero: modales en columna con cuerpo desplazable y alto con --beep-vh.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { fetchAuth } from '../../utils/sesion';
import { API_BASE } from '../../config/api';
import { fmtDinero } from '../../utils/dinero';

const PP = "'Poppins', sans-serif";
const C = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#8a8f95', rojo: '#e05c5c', verde: '#1D9E75', ambar: '#b45309' };
const ESTADOS = {
  propuesta:  { label: 'Propuesta',  bg: '#fffbeb', color: C.ambar },
  confirmada: { label: 'En el almacén', bg: '#e8faf5', color: C.verde },
  cancelada:  { label: 'Cancelada',  bg: '#f1f3f6', color: C.grisClaro },
};
const fmtCant = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });
const fmtFecha = (v) => { try { return new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const fmtDia = (v) => { if (!v) return '—'; try { return new Date(String(v).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const card = { background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };
const inputBase = { padding: '10px 12px', minHeight: 44, borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: C.grisClaro, fontWeight: 700, marginBottom: 4, marginTop: 10, display: 'block', textTransform: 'uppercase' };
const btn = (fondo, color = '#fff', borde = 'none') => ({ padding: '10px 14px', minHeight: 44, borderRadius: 10, background: fondo, color, border: borde, fontWeight: 700, cursor: 'pointer', fontFamily: PP, fontSize: 13 });
const btnMini = (color = C.grisClaro) => ({ padding: '6px 10px', minHeight: 32, borderRadius: 8, background: '#fff', color, border: `1px solid ${color}`, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: PP });

async function llamar(path, opts = {}) {
  const r = await fetchAuth(`${API_BASE}${path}`, opts);
  const d = await r.json().catch(() => ({}));
  if (!r.ok || !d.ok) throw Object.assign(new Error(d.error || 'No se pudo completar la operacion'), { status: r.status });
  return d;
}

// ---------- armazon de modal (portal, celular primero) ----------
const overlay = { position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 12 };
function Modal({ titulo, onCerrar, children, pie, ancho = 520 }) {
  return createPortal(
    <div style={overlay} onClick={onCerrar}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: ancho, maxHeight: 'calc(var(--beep-vh) * 94)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 18px 8px', fontSize: 16, fontWeight: 800, color: C.oscuro, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{titulo}</span>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: C.grisClaro, width: 36, height: 36, flexShrink: 0 }}>×</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 18px 12px' }}>{children}</div>
        {pie && <div style={{ padding: '10px 18px 14px', display: 'flex', gap: 8, borderTop: '1px solid #eef0f3', flexWrap: 'wrap' }}>{pie}</div>}
      </div>
    </div>,
    document.body
  );
}
const Aviso = ({ tipo = 'error', children }) => (
  <div style={{ marginTop: 10, background: tipo === 'error' ? '#fde8e8' : tipo === 'ok' ? '#e8faf5' : '#fffbeb', color: tipo === 'error' ? '#b02020' : tipo === 'ok' ? C.verde : C.ambar, borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 600 }}>{children}</div>
);

// ---------- buscador de catalogo (Syscom + Mis Productos, motor de La Tienda) ----------
function BuscarCatalogo({ onElegir, autoFocus = true }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const [buscando, setBuscando] = useState(false);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRes([]); return undefined; }
    let vivo = true; setBuscando(true);
    const h = setTimeout(async () => {
      try {
        const d = await llamar(`/api/tienda/buscar?limit=10&q=${encodeURIComponent(t)}`);
        if (!vivo) return;
        setRes((d.productos || []).map(p => ({ tipo: p.tipo === 'syscom_producto' ? 'syscom_producto' : 'beep_producto', referencia_id: String(p.referencia_id), nombre: p.nombre, marca: p.marca, modelo: p.modelo, costo_catalogo: p.costo_mxn })).filter(p => p.tipo === 'syscom_producto' || /^\d+$/.test(p.referencia_id)));
      } catch (e) { if (vivo && e.code !== 'SESION_EXPIRADA') setRes([]); }
      finally { if (vivo) setBuscando(false); }
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q]);
  return (
    <div>
      <input autoFocus={autoFocus} value={q} onChange={(e) => setQ(e.target.value)} style={inputBase} placeholder="Modelo, nombre o marca en el catálogo…" inputMode="search" autoComplete="off" />
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>{q.trim().length < 2 ? 'Escribe al menos 2 letras.' : buscando ? 'Buscando…' : `${res.length} resultado(s)`}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
        {res.map(p => (
          <button key={`${p.tipo}-${p.referencia_id}`} type="button" onClick={() => onElegir(p)} style={{ textAlign: 'left', padding: '8px 10px', minHeight: 44, background: '#fff', border: '1.5px solid #e5e8ec', borderRadius: 10, cursor: 'pointer', fontFamily: PP }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.gris, overflowWrap: 'anywhere' }}>{p.nombre}</div>
            <div style={{ fontSize: 11, color: C.grisClaro }}>{p.tipo === 'syscom_producto' ? 'Syscom' : 'Mis Productos'}{p.marca ? ` · ${p.marca}` : ''}{p.modelo ? ` · ${p.modelo}` : ''}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================
// PESTANA
// ============================================
export default function PestanaCompras({ esCelular }) {
  const [compras, setCompras] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [nueva, setNueva] = useState(false);
  const [traer, setTraer] = useState(false); // Traer de Syscom
  // Hoja de compras inteligente. Se abre sola si una tarjeta de La Tienda (cara inventario) mando aqui.
  const [hoja, setHoja] = useState(() => { try { return !!sessionStorage.getItem('beep_abrir_hoja_compras'); } catch { return false; } });
  // El producto desde el que se llego (modelo): la hoja arranca filtrada a el.
  const [hojaFiltro] = useState(() => { try { const v = JSON.parse(sessionStorage.getItem('beep_abrir_hoja_compras') || 'null'); return (v && v.modelo) || ''; } catch { return ''; } });
  useEffect(() => { try { sessionStorage.removeItem('beep_abrir_hoja_compras'); } catch {} }, []);
  const [abierta, setAbierta] = useState(null); // id
  const [aviso, setAviso] = useState('');

  const recargar = useCallback(async () => {
    setCargando(true); setError('');
    try { setCompras((await llamar('/api/compras')).compras || []); }
    catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
    finally { setCargando(false); }
  }, []);
  useEffect(() => { recargar(); }, [recargar]);

  const visibles = useMemo(() => compras.filter(c => filtro === 'todas' || c.estado === filtro), [compras, filtro]);
  const chip = (id, label, color = C.oscuro) => (
    <button key={id} onClick={() => setFiltro(id)} style={{ padding: '6px 12px', minHeight: 32, borderRadius: 14, background: filtro === id ? color : '#fff', color: filtro === id ? '#fff' : color, border: `1.5px solid ${color}`, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: PP }}>{label}</button>
  );
  const hecho = (msg) => { setAviso(msg); recargar(); setTimeout(() => setAviso(''), 5000); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: C.grisClaro, lineHeight: 1.5 }}>
        <strong>Toda compra entra al almacén</strong> a su costo real (promedio, con el flete repartido). Nada se asigna a proyectos desde aquí: cada expediente <strong>trae del almacén</strong> lo suyo. Antes de comprar, revisa la <strong>Hoja de compras</strong>: solo lista lo que de verdad falta.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {chip('todas', 'Todas')}{chip('propuesta', 'Propuestas', C.ambar)}{chip('confirmada', 'En el almacén', C.verde)}{chip('cancelada', 'Canceladas', C.grisClaro)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setHoja(true)} style={{ ...btn(C.oscuro), flex: 1, minWidth: 150 }}>📝 Hoja de compras</button>
        <button onClick={() => setTraer(true)} style={{ ...btn(C.principal), flex: 1, minWidth: 150 }}>⬇️ Traer de Syscom</button>
        <button onClick={() => setNueva(true)} style={{ ...btn('#fff', C.oscuro, `1.5px solid ${C.principal}`), flex: 1, minWidth: 150 }}>🛒 Nueva compra (XML o a mano)</button>
      </div>
      {aviso && <Aviso tipo="ok">{aviso}</Aviso>}
      {error && <Aviso>{error}</Aviso>}
      {cargando && compras.length === 0 && <div style={{ fontSize: 12, color: C.grisClaro }}>Cargando compras…</div>}
      {!cargando && compras.length === 0 && !error && <div style={{ ...card, textAlign: 'center', color: C.grisClaro, fontSize: 13, padding: 24 }}>Todavía no hay compras registradas.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visibles.map(c => {
          const e = ESTADOS[c.estado] || ESTADOS.propuesta;
          return (
            <button key={c.id} onClick={() => setAbierta(c.id)} style={{ ...card, textAlign: 'left', border: 'none', cursor: 'pointer', fontFamily: PP, width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: C.grisClaro, fontWeight: 700 }}>{c.folio} · {fmtDia(c.fecha)} · {c.tipo_comprobante === 'cfdi' ? `CFDI ${c.cfdi_serie || ''}${c.cfdi_folio || ''}`.trim() : 'Manual'}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: C.gris, overflowWrap: 'anywhere' }}>{c.proveedor}</div>
                  <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 2 }}>{c.lineas_n} línea{c.lineas_n === 1 ? '' : 's'} · {fmtCant(c.piezas)} pieza{Number(c.piezas) === 1 ? '' : 's'}{c.tiene_comprobante ? ' · comprobante' : ''}{c.estado === 'confirmada' ? ' · entró al almacén' : ''}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 700, background: e.bg, color: e.color, padding: '3px 8px', borderRadius: 10 }}>{e.label}</span>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.oscuro, marginTop: 6 }}>{fmtDinero(c.costo_material)}</div>
                  {Number(c.costo_flete) > 0 && <div style={{ fontSize: 10.5, color: C.grisClaro }}>+ flete {fmtDinero(c.costo_flete)}</div>}
                </div>
              </div>
              {c.estado === 'propuesta' && c.lineas_sin_vincular > 0 && <div style={{ fontSize: 11, color: C.ambar, fontWeight: 600, marginTop: 6 }}>⚠️ {c.lineas_sin_vincular} línea(s) sin vincular al catálogo</div>}
              {Number(c.advertencias_n) > 0 && <div style={{ fontSize: 11, color: C.ambar, fontWeight: 600, marginTop: 4 }}>⚠️ {c.advertencias_n} advertencia(s): el CFDI no se leyó completo o no cuadra</div>}
            </button>
          );
        })}
      </div>
      {hoja && <ModalHojaCompras esCelular={esCelular} filtroInicial={hojaFiltro} onCerrar={() => setHoja(false)} onTraer={() => { setHoja(false); setTraer(true); }} />}
      {nueva && <ModalNuevaCompra onCerrar={() => setNueva(false)} onCreada={(id) => { setNueva(false); recargar(); setAbierta(id); }} />}
      {traer && <ModalTraerSyscom onCerrar={() => setTraer(false)} onCreada={(id) => { setTraer(false); recargar(); setAbierta(id); }} onAbrir={(id) => { setTraer(false); setAbierta(id); }} />}
      {abierta && <DetalleCompra id={abierta} esCelular={esCelular} onCerrar={() => { setAbierta(null); recargar(); }} onHecho={hecho} />}
    </div>
  );
}

// ============================================
// HOJA DE COMPRAS INTELIGENTE: lo que piden los proyectos contra lo que HAY en el almacen.
// ============================================
const SENALES = {
  cubierto: { label: 'YA ESTÁ EN EL ALMACÉN', corto: 'En almacén', bg: '#e8faf5', color: C.verde, icono: '🟢' },
  parcial:  { label: 'HAY UNA PARTE', corto: 'Parcial', bg: '#fffbeb', color: C.ambar, icono: '🟡' },
  faltante: { label: 'NO HAY: COMPRAR', corto: 'Comprar', bg: '#fde8e8', color: '#b02020', icono: '🔴' },
};
function ModalHojaCompras({ esCelular, onCerrar, onTraer, filtroInicial = '' }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [soloComprar, setSoloComprar] = useState(false);
  const [texto, setTexto] = useState(filtroInicial || '');
  const [abiertos, setAbiertos] = useState({});
  useEffect(() => {
    let vivo = true;
    (async () => {
      try { const r = await llamar('/api/compras/hoja'); if (vivo) setD(r); }
      catch (e) { if (vivo && e.code !== 'SESION_EXPIRADA') setError(e.message); }
    })();
    return () => { vivo = false; };
  }, []);
  const productos = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return ((d && d.productos) || []).filter(p => (!soloComprar || Number(p.por_comprar) > 0) && (!t || `${p.nombre || ''} ${p.modelo || ''} ${p.marca || ''}`.toLowerCase().includes(t)));
  }, [d, soloComprar, texto]);
  const res = d && d.resumen;
  return (
    <Modal ancho={760} titulo="📝 Hoja de compras" onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={{ ...btn('#f1f3f6', C.gris) }}>Cerrar</button>
      <div style={{ flex: 1 }} />
      <button onClick={onTraer} style={btn(C.principal)}>⬇️ Traer de Syscom</button>
    </>}>
      <div style={{ fontSize: 12, color: C.grisClaro, lineHeight: 1.5 }}>Lo que piden los proyectos (checklist por revisar o en falta) frente a lo que <strong>hay hoy en el almacén</strong>. Solo se compra el <strong>faltante real</strong> = requerido − existencia. Lo que ya viene en una compra en propuesta se descuenta y se avisa.</div>
      {error && <Aviso>{error}</Aviso>}
      {!d && !error && <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 10 }}>Revisando el almacén…</div>}
      {d && <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Filtrar por modelo, nombre o marca…" style={{ ...inputBase, marginTop: 10 }} />}
      {res && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, background: SENALES.cubierto.bg, color: SENALES.cubierto.color, padding: '4px 10px', borderRadius: 12 }}>🟢 {res.cubiertos} ya en almacén</span>
          <span style={{ fontSize: 11, fontWeight: 700, background: SENALES.parcial.bg, color: SENALES.parcial.color, padding: '4px 10px', borderRadius: 12 }}>🟡 {res.parciales} en parte</span>
          <span style={{ fontSize: 11, fontWeight: 700, background: SENALES.faltante.bg, color: SENALES.faltante.color, padding: '4px 10px', borderRadius: 12 }}>🔴 {res.faltantes} sin existencia</span>
          <label style={{ marginLeft: 'auto', fontSize: 12, color: C.gris, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', minHeight: 32 }}>
            <input type="checkbox" checked={soloComprar} onChange={(e) => setSoloComprar(e.target.checked)} /> Solo lo que hay que comprar ({res.por_comprar})
          </label>
        </div>
      )}
      {d && productos.length === 0 && <div style={{ ...card, textAlign: 'center', color: C.grisClaro, fontSize: 13, padding: 20, marginTop: 10 }}>{soloComprar ? 'Nada que comprar: todo lo que piden los proyectos ya está en el almacén o ya viene en una propuesta.' : 'Ningún proyecto tiene material pendiente de surtir.'}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {productos.map(p => {
          const s = SENALES[p.senal] || SENALES.faltante;
          const k = `${p.tipo}-${p.referencia_id}`;
          const abierto = !!abiertos[k];
          return (
            <div key={k} style={{ ...card, padding: 12, borderLeft: `6px solid ${s.color}` }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {p.imagen_url && <img src={p.imagen_url} alt="" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: 8, border: '1px solid #eef0f3', background: '#fff', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase' }}>{p.tipo === 'syscom_producto' ? 'Syscom' : 'Mis Productos'}{p.marca ? ` · ${p.marca}` : ''}{p.modelo ? ` · ${p.modelo}` : ''}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.gris, overflowWrap: 'anywhere' }}>{p.nombre}</div>
                  <div style={{ marginTop: 6, display: 'inline-block', fontSize: 11, fontWeight: 800, background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 10 }}>{s.icono} {s.label}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: esCelular ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                <Dato etiqueta="Piden los proyectos" valor={`${fmtCant(p.requerido)} ${p.unidad === 'metro' ? 'm' : 'pza'}`} sub={`${p.proyectos_n} expediente${p.proyectos_n === 1 ? '' : 's'}`} />
                <Dato etiqueta="Hay en el almacén" valor={fmtCant(p.existencia)} color={Number(p.existencia) > 0 ? C.verde : '#b02020'} sub={`disponible ${fmtCant(p.disponible)} · apartado ${fmtCant(p.apartado)}`} />
                <Dato etiqueta="Faltante real" valor={fmtCant(p.faltante_real)} color={Number(p.faltante_real) > 0 ? C.ambar : C.verde} sub="requerido − existencia" />
                <Dato etiqueta="Por comprar" valor={fmtCant(p.por_comprar)} color={Number(p.por_comprar) > 0 ? '#b02020' : C.verde} sub={Number(p.en_propuesta) > 0 ? `${fmtCant(p.en_propuesta)} ya en propuesta ${p.propuestas_folios || ''}` : (p.stock_proveedor != null ? `Syscom tiene ${p.stock_proveedor}` : ' ')} />
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={() => setAbiertos(a => ({ ...a, [k]: !abierto }))} style={btnMini(C.oscuro)}>{abierto ? 'Ocultar expedientes' : `Ver expedientes (${p.proyectos_n})`}</button>
                <button onClick={() => navigate(`/tienda/producto/${encodeURIComponent(p.id_unico)}`)} style={btnMini(C.principal)}>Ver en la Tienda</button>
              </div>
              {abierto && (
                <div style={{ marginTop: 8, borderTop: '1px solid #eef0f3', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(p.proyectos || []).map(x => (
                    <div key={x.solicitud_item_id} style={{ fontSize: 12, color: C.gris, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                      <span>{x.expediente_folio || x.solicitud_folio}{x.cliente ? ` · ${x.cliente}` : ''}{x.sucursal ? ` · ${x.sucursal}` : ''} <span style={{ color: C.grisClaro }}>· {x.estado_linea === 'falta' ? 'en falta' : 'por revisar'}</span></span>
                      <strong style={{ color: C.oscuro }}>{fmtCant(x.cantidad)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {d && d.sin_catalogo && d.sin_catalogo.length > 0 && (
        <div style={{ ...card, marginTop: 10, padding: 12 }}>
          <div style={{ fontSize: 11, color: C.ambar, fontWeight: 800 }}>⚠️ {d.sin_catalogo.length} línea(s) sin producto del catálogo: no se pueden comparar con el almacén</div>
          {d.sin_catalogo.map(x => <div key={x.solicitud_item_id} style={{ fontSize: 12, color: C.gris, marginTop: 4 }}>{x.expediente_folio || x.solicitud_folio} · {x.nombre} · {fmtCant(x.cantidad)}</div>)}
        </div>
      )}
    </Modal>
  );
}
function Dato({ etiqueta, valor, sub, color }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 10, color: C.grisClaro, fontWeight: 700, textTransform: 'uppercase' }}>{etiqueta}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: color || C.oscuro }}>{valor}</div>
      {sub && <div style={{ fontSize: 10.5, color: C.grisClaro, overflowWrap: 'anywhere' }}>{sub}</div>}
    </div>
  );
}

// ============================================
// TRAER DE SYSCOM: lista las facturas de la cuenta desde la API y las importa como propuesta
// ============================================
function ModalTraerSyscom({ onCerrar, onCreada, onAbrir }) {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(String(anioActual));
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  const [importando, setImportando] = useState(null); // folio
  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const qs = new URLSearchParams({ anio, pagina: String(pagina) });
      if (busqueda.trim()) qs.set('busqueda', busqueda.trim().slice(0, 40));
      setD(await llamar(`/api/compras/syscom/facturas?${qs.toString()}`));
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
    finally { setCargando(false); }
  }, [anio, pagina, busqueda]);
  useEffect(() => { cargar(); }, [cargar]);
  const importar = async (f) => {
    if (importando) return;
    setImportando(f.folio_factura); setError('');
    try {
      const r = await llamar('/api/compras/syscom/importar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ folio: f.folio_factura, fecha: f.fecha }) });
      onCreada(r.compra.id);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setImportando(null); }
  };
  const anios = [anioActual, anioActual - 1, anioActual - 2].map(String);
  const facturas = (d && d.facturas) || [];
  return (
    <Modal ancho={640} titulo="⬇️ Traer de Syscom" onCerrar={onCerrar} pie={<>
      <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina <= 1 || cargando} style={{ ...btn('#f1f3f6', C.gris), opacity: pagina <= 1 ? 0.5 : 1 }}>◀</button>
      <div style={{ flex: 1, textAlign: 'center', fontSize: 12, color: C.grisClaro }}>{d ? `página ${d.pagina} de ${d.paginas || 1} · ${d.total} factura(s)` : ''}</div>
      <button onClick={() => setPagina(p => p + 1)} disabled={!d || pagina >= (d.paginas || 1) || cargando} style={{ ...btn('#f1f3f6', C.gris), opacity: (!d || pagina >= (d.paginas || 1)) ? 0.5 : 1 }}>▶</button>
      <button onClick={onCerrar} style={btn('#f1f3f6', C.gris)}>Cerrar</button>
    </>}>
      <div style={{ fontSize: 12, color: C.grisClaro, lineHeight: 1.5 }}>Facturas de tu cuenta de Syscom, leídas de su API. Al importar una, se crea la <strong>propuesta</strong> con cada producto vinculado a tu catálogo por su código Syscom; nada entra al almacén hasta que confirmes.</div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <select value={anio} onChange={(e) => { setAnio(e.target.value); setPagina(1); }} style={{ ...inputBase, width: 110 }}>{anios.map(a => <option key={a} value={a}>{a}</option>)}</select>
        <input value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }} placeholder="Folio (FA26/…) o fecha (2026-10)" style={{ ...inputBase, flex: 1, minWidth: 160 }} maxLength={40} />
      </div>
      {error && <Aviso>{error}</Aviso>}
      {cargando && <div style={{ fontSize: 12, color: C.grisClaro, marginTop: 10 }}>Consultando a Syscom…</div>}
      {!cargando && d && facturas.length === 0 && <div style={{ ...card, textAlign: 'center', color: C.grisClaro, fontSize: 13, padding: 20, marginTop: 10 }}>Syscom no devolvió facturas con ese filtro.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {facturas.map(f => {
          const cancelada = /cancelad/i.test(f.estatus || '');
          const noMxn = f.moneda && f.moneda !== 'MXN';
          return (
            <div key={f.folio_factura} style={{ ...card, padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: C.oscuro }}>{f.folio_factura} <span style={{ fontSize: 11, color: C.grisClaro, fontWeight: 600 }}>· {fmtFecha(f.fecha)}{f.folio_pedido ? ` · pedido ${f.folio_pedido}` : ''}</span></div>
                <div style={{ fontSize: 11.5, color: C.gris, overflowWrap: 'anywhere' }}>{f.texto}</div>
                <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 2 }}>{f.moneda} {fmtDinero(f.total)} · {f.estatus}{f.entregada ? ' · entregada' : ''}{noMxn ? ' · no se importa (sin tipo de cambio)' : ''}</div>
              </div>
              {f.ya_registrada
                ? <button onClick={() => onAbrir(f.compra_id)} style={btn('#fff', C.oscuro, `1.5px solid ${C.principal}`)}>Ya registrada · {f.compra_folio} ({(ESTADOS[f.compra_estado] || {}).label || f.compra_estado})</button>
                : <button onClick={() => importar(f)} disabled={!!importando || cancelada || noMxn} style={{ ...btn(C.verde), opacity: (importando || cancelada || noMxn) ? 0.5 : 1 }}>{importando === f.folio_factura ? 'Importando…' : cancelada ? 'Cancelada' : 'Importar'}</button>}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

// ============================================
// NUEVA COMPRA (XML o a mano)
// ============================================
function ModalNuevaCompra({ onCerrar, onCreada }) {
  const [proveedor, setProveedor] = useState('');
  const [fecha, setFecha] = useState(new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }));
  const [flete, setFlete] = useState('');
  const [nota, setNota] = useState('');
  const [archivo, setArchivo] = useState(null);
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const esXml = archivo && (/\.xml$/i.test(archivo.name) || /xml/i.test(archivo.type));
  const listo = (esXml || proveedor.trim()) && !enviando;

  const crear = async () => {
    if (!listo) return;
    setEnviando(true); setError('');
    try {
      const fd = new FormData();
      if (proveedor.trim()) fd.append('proveedor', proveedor.trim());
      fd.append('fecha', fecha);
      if (flete.trim()) fd.append('costo_flete_manual', flete.trim().replace(/,/g, ''));
      if (nota.trim()) fd.append('nota', nota.trim());
      if (archivo) fd.append('archivo', archivo);
      const d = await llamar('/api/compras', { method: 'POST', body: fd });
      onCreada(d.compra.id);
    } catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); setEnviando(false); }
  };
  return (
    <Modal titulo="🛒 Nueva compra" onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={{ ...btn('#f1f3f6', C.gris), flex: 1 }}>Cancelar</button>
      <button onClick={crear} disabled={!listo} style={{ ...btn(C.verde), flex: 1, opacity: listo ? 1 : 0.5 }}>{enviando ? 'Leyendo…' : 'Crear propuesta'}</button>
    </>}>
      <label style={labelStyle}>Comprobante (XML del CFDI, PDF o foto)</label>
      <input type="file" accept=".xml,application/xml,text/xml,application/pdf,image/jpeg,image/png" onChange={(e) => setArchivo(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ ...inputBase, padding: 8 }} />
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>{esXml ? 'Se leerá el CFDI: proveedor, fecha, líneas y flete salen del XML y se emparejan por código.' : 'Si es de Syscom, mejor usa "Traer de Syscom" (o sube el XML del CFDI). Para otros proveedores captura las líneas a mano después.'}</div>
      <label style={labelStyle}>Proveedor{esXml ? ' (opcional, lo trae el CFDI)' : ''}</label>
      <input value={proveedor} onChange={(e) => setProveedor(e.target.value)} style={inputBase} placeholder="Syscom, Solar Boutique, Amazon…" />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}><label style={labelStyle}>Fecha de compra</label><input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputBase} /></div>
        <div style={{ flex: 1 }}><label style={labelStyle}>Flete a mano (MXN)</label><input inputMode="decimal" value={flete} onChange={(e) => setFlete(e.target.value)} style={inputBase} placeholder="0.00" /></div>
      </div>
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>La garantía del proveedor de cada producto corre desde esta fecha.</div>
      <label style={labelStyle}>Nota (opcional)</label>
      <input value={nota} onChange={(e) => setNota(e.target.value)} style={inputBase} placeholder="Pedido, orden, observaciones…" />
      {error && <Aviso>{error}</Aviso>}
    </Modal>
  );
}

// ============================================
// DETALLE / PROPUESTA
// ============================================
function DetalleCompra({ id, esCelular, onCerrar, onHecho }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [vincular, setVincular] = useState(null); // linea id
  const [agregar, setAgregar] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [aceptar, setAceptar] = useState(false); // la persona vio las advertencias del CFDI y confirma a proposito
  const cargar = useCallback(async () => {
    try { setD(await llamar(`/api/compras/${id}`)); setError(''); }
    catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);

  const accion = async (fn, okMsg) => {
    if (ocupado) return;
    setOcupado(true); setError('');
    try { const r = await fn(); if (r && r.compra) setD(r); else await cargar(); if (okMsg) onHecho(okMsg); }
    catch (e) { if (e.code !== 'SESION_EXPIRADA') setError(e.message); }
    finally { setOcupado(false); }
  };

  if (!d && !error) return <Modal titulo="Compra" onCerrar={onCerrar}><div style={{ fontSize: 12, color: C.grisClaro }}>Cargando…</div></Modal>;
  if (!d) return <Modal titulo="Compra" onCerrar={onCerrar}><Aviso>{error}</Aviso></Modal>;
  const { compra, lineas, comprobantes } = d;
  const editable = compra.estado === 'propuesta';
  const sinVincular = lineas.filter(l => !l.es_flete && !l.tipo);
  const advertencias = Array.isArray(compra.advertencias) ? compra.advertencias : [];
  const bloqueaConfirmar = sinVincular.length > 0 || lineas.length === 0 || (advertencias.length > 0 && !aceptar);
  const e = ESTADOS[compra.estado] || ESTADOS.propuesta;

  const verComprobante = async (c) => {
    try {
      const r = await fetchAuth(`${API_BASE}/api/compras/${id}/comprobante/${c.id}`);
      if (!r.ok) throw new Error('No se pudo abrir el comprobante');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (er) { setError(er.message); }
  };

  const confirmar = () => accion(async () => {
    const r = await llamar(`/api/compras/${id}/confirmar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ aceptar_advertencias: advertencias.length > 0 && aceptar }) });
    setConfirmando(false);
    onHecho(`${compra.folio} confirmada: ${r.productos} producto(s) entraron al almacén con su costo real y su garantía del proveedor.`);
    return r;
  });

  return (
    <Modal ancho={720} titulo={<span>{compra.folio} · {compra.proveedor} <span style={{ fontSize: 10.5, fontWeight: 700, background: e.bg, color: e.color, padding: '3px 8px', borderRadius: 10, marginLeft: 6 }}>{e.label}</span></span>} onCerrar={onCerrar} pie={editable ? <>
      <button onClick={() => accion(() => llamar(`/api/compras/${id}/cancelar`, { method: 'POST' }), `${compra.folio} cancelada.`)} disabled={ocupado} style={{ ...btn('#fff', C.rojo, `1.5px solid ${C.rojo}`) }}>Cancelar compra</button>
      <div style={{ flex: 1 }} />
      {!confirmando
        ? <button onClick={() => setConfirmando(true)} disabled={ocupado || bloqueaConfirmar} style={{ ...btn(C.verde), opacity: bloqueaConfirmar ? 0.5 : 1 }}>Confirmar: entra al almacén</button>
        : <>
          <button onClick={() => setConfirmando(false)} style={btn('#f1f3f6', C.gris)}>No</button>
          <button onClick={confirmar} disabled={ocupado} style={btn(C.verde)}>{ocupado ? 'Confirmando…' : 'Sí, confirmar: todo entra al almacén'}</button>
        </>}
    </> : <button onClick={onCerrar} style={{ ...btn('#f1f3f6', C.gris), flex: 1 }}>Cerrar</button>}>

      {/* cabecera */}
      <div style={{ fontSize: 12, color: C.grisClaro, lineHeight: 1.6 }}>
        Compra del {fmtDia(compra.fecha)} · {compra.tipo_comprobante === 'cfdi' ? <>CFDI {compra.cfdi_serie}{compra.cfdi_folio}{compra.cfdi_uuid ? ` · ${compra.cfdi_uuid.slice(0, 8)}…` : ''}{compra.moneda !== 'MXN' ? ` · ${compra.moneda} a ${compra.tipo_cambio}` : ''}</> : 'Compra manual'}
        {compra.creado_por_nombre ? ` · creada por ${compra.creado_por_nombre}` : ''}
        {compra.confirmada_en ? ` · entró al almacén el ${fmtFecha(compra.confirmada_en)}${compra.confirmada_firma ? ` (${compra.confirmada_firma})` : ''}` : ''}
        {comprobantes.map(c => <span key={c.id}> · <button onClick={() => verComprobante(c)} style={{ border: 'none', background: 'none', color: C.principal, fontWeight: 700, cursor: 'pointer', fontFamily: PP, padding: 0, fontSize: 12 }}>📎 {c.nombre_archivo}</button></span>)}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        <span>Material <strong style={{ color: C.oscuro }}>{fmtDinero(compra.costo_material)}</strong></span>
        <span>Flete <strong style={{ color: C.oscuro }}>{fmtDinero(compra.costo_flete)}</strong>{editable && <FleteManual id={id} valor={compra.costo_flete_manual} onGuardado={cargar} />}</span>
      </div>
      <div style={{ fontSize: 11, color: C.grisClaro, marginTop: 4 }}>{editable ? 'Al confirmar, cada producto entra al almacén a su costo unitario más su parte del flete (repartido por peso en costo), y queda como lote con la garantía del proveedor desde la fecha de compra.' : 'Cada línea entró al almacén con el flete repartido; la garantía del proveedor corre desde la fecha de compra.'}</div>
      {editable && sinVincular.length > 0 && <Aviso tipo="ambar">⚠️ {sinVincular.length} línea(s) sin vincular al catálogo. Vincúlalas (o márcalas como flete) para poder confirmar.</Aviso>}
      {advertencias.length > 0 && (
        <Aviso tipo="ambar">
          <div style={{ fontWeight: 800 }}>⚠️ El CFDI no se leyó completo o no cuadra ({advertencias.length}):</div>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>{advertencias.map((a, i) => <li key={i} style={{ marginTop: 2 }}>{a.mensaje}</li>)}</ul>
          {editable && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, cursor: 'pointer', fontWeight: 700 }}>
              <input type="checkbox" checked={aceptar} onChange={(ev) => setAceptar(ev.target.checked)} style={{ marginTop: 2 }} />
              <span>Revisé las advertencias contra la factura: confirmo la compra solo con lo que sí se leyó.</span>
            </label>
          )}
        </Aviso>
      )}
      {error && <Aviso>{error}</Aviso>}

      {/* lineas */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        {lineas.map(l => (
          <LineaCompra key={l.id} id={id} l={l} editable={editable} onAccion={accion} onVincular={() => setVincular(l.id)} />
        ))}
        {lineas.length === 0 && <div style={{ fontSize: 12, color: C.grisClaro }}>Sin líneas todavía.</div>}
      </div>
      {editable && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button onClick={() => setAgregar('producto')} style={btn('#fff', C.oscuro, `1.5px solid ${C.principal}`)}>+ Producto del catálogo</button>
          <button onClick={() => setAgregar('flete')} style={btn('#fff', C.oscuro, `1.5px solid ${C.principal}`)}>+ Línea de flete</button>
        </div>
      )}

      {vincular && <ModalVincular onCerrar={() => setVincular(null)} onElegir={(p) => { setVincular(null); accion(() => llamar(`/api/compras/${id}/lineas/${vincular}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tipo: p.tipo, referencia_id: p.referencia_id }) })); }} />}
      {agregar && <ModalAgregarLinea tipo={agregar} onCerrar={() => setAgregar(false)} onAgregar={(body) => { setAgregar(false); accion(() => llamar(`/api/compras/${id}/lineas`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })); }} />}
    </Modal>
  );
}

function FleteManual({ id, valor, onGuardado }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(String(valor || '0'));
  if (!edit) return <button onClick={() => setEdit(true)} style={{ ...btnMini(C.principal), marginLeft: 6 }}>flete a mano: {fmtDinero(valor)}</button>;
  const guardar = async () => { try { await llamar(`/api/compras/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ costo_flete_manual: v.replace(/,/g, '') }) }); setEdit(false); onGuardado(); } catch (e) { /* lo pinta el detalle al recargar */ } };
  return <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6 }}><input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} style={{ ...inputBase, width: 110, minHeight: 32, padding: '4px 8px', fontSize: 13 }} /><button onClick={guardar} style={btnMini(C.verde)}>OK</button></span>;
}

function LineaCompra({ id, l, editable, onAccion, onVincular }) {
  const quitar = () => onAccion(() => llamar(`/api/compras/${id}/lineas/${l.id}`, { method: 'DELETE' }));
  const marcarFlete = (v) => onAccion(() => llamar(`/api/compras/${id}/lineas/${l.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ es_flete: v }) }));
  const colorBarra = l.es_flete ? C.grisClaro : l.tipo ? (l.tipo === 'syscom_producto' ? C.principal : '#5cd4b0') : C.ambar;
  const confirmada = !editable && l.inventario_entrada_id;

  return (
    <div style={{ ...card, padding: 12, display: 'flex', gap: 10 }}>
      <div style={{ width: 6, background: colorBarra, borderRadius: 3, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            {l.es_flete
              ? <div style={{ fontSize: 13, fontWeight: 700, color: C.gris }}>🚚 Flete · {l.descripcion || l.clave_proveedor}</div>
              : l.tipo
                ? <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    {l.catalogo_imagen && <img src={l.catalogo_imagen} alt="" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, border: '1px solid #eef0f3', background: '#fff', flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10.5, color: colorBarra, fontWeight: 700, textTransform: 'uppercase' }}>{l.tipo === 'syscom_producto' ? 'Syscom' : 'Mis Productos'}{l.catalogo_modelo ? ` · ${l.catalogo_modelo}` : l.clave_proveedor ? ` · ${l.clave_proveedor}` : ''}{l.catalogo_marca ? ` · ${l.catalogo_marca}` : ''}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.gris, overflowWrap: 'anywhere' }}>{l.nombre_catalogo}</div>
                    </div>
                  </div>
                : <><div style={{ fontSize: 10.5, color: C.ambar, fontWeight: 700 }}>⚠️ SIN VINCULAR · {l.clave_proveedor || 'sin clave'}</div><div style={{ fontSize: 12.5, color: C.gris, overflowWrap: 'anywhere' }}>{l.descripcion}</div></>}
            {!l.es_flete && l.descripcion && l.tipo && <div style={{ fontSize: 11, color: C.grisClaro, overflowWrap: 'anywhere' }}>{l.descripcion}</div>}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0, fontSize: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.oscuro }}>{fmtCant(l.cantidad)}</div>
            <div style={{ color: C.grisClaro }}>× {fmtDinero(l.costo_unitario)}</div>
            <div style={{ fontWeight: 700, color: C.gris }}>{fmtDinero(l.costo_importe)}</div>
          </div>
        </div>

        {!l.es_flete && l.tipo && (
          <div style={{ marginTop: 8, borderTop: '1px solid #eef0f3', paddingTop: 8, fontSize: 11.5, color: C.grisClaro, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>En el almacén ahora: <strong style={{ color: Number(l.existencia_actual) > 0 ? C.verde : C.gris }}>{fmtCant(l.existencia_actual)}</strong>{l.costo_promedio_actual != null ? <> · promedio <strong style={{ color: C.gris }}>{fmtDinero(l.costo_promedio_actual)}</strong></> : null}</span>
            {confirmada && <span>Entró a <strong style={{ color: C.gris }}>{fmtDinero(l.costo_unitario_con_flete)}</strong> c/u{Number(l.costo_flete_prorrateado) > 0 ? ` (incluye flete ${fmtDinero(l.costo_flete_prorrateado)})` : ''}</span>}
            {confirmada && <span>Garantía del proveedor: <strong style={{ color: l.garantia_vence ? C.verde : C.ambar }}>{l.garantia_vence ? `hasta ${fmtDia(l.garantia_vence)} (${l.garantia_meses} meses)` : 'por confirmar'}</strong>{l.lote_restante != null ? ` · quedan ${fmtCant(l.lote_restante)} de este lote` : ''}</span>}
          </div>
        )}
        {editable && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {!l.es_flete && <button onClick={onVincular} style={btnMini(C.principal)}>{l.tipo ? 'Cambiar producto' : 'Vincular al catálogo'}</button>}
            <button onClick={() => marcarFlete(!l.es_flete)} style={btnMini(C.grisClaro)}>{l.es_flete ? 'No es flete' : 'Es flete'}</button>
            <button onClick={quitar} style={btnMini(C.rojo)}>Quitar línea</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ModalVincular({ onCerrar, onElegir }) {
  return <Modal titulo="Vincular al catálogo" onCerrar={onCerrar}><BuscarCatalogo onElegir={onElegir} /></Modal>;
}

function ModalAgregarLinea({ tipo, onCerrar, onAgregar }) {
  const [producto, setProducto] = useState(null);
  const [cantidad, setCantidad] = useState(tipo === 'flete' ? '1' : '');
  const [costo, setCosto] = useState('');
  const [descripcion, setDescripcion] = useState(tipo === 'flete' ? 'Flete' : '');
  const num = (s) => /^\d+(\.\d+)?$/.test(String(s).trim().replace(/,/g, ''));
  const listo = (tipo === 'flete' || producto) && num(cantidad) && Number(cantidad) > 0 && num(costo);
  return (
    <Modal titulo={tipo === 'flete' ? '🚚 Línea de flete' : '+ Producto del catálogo'} onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={{ ...btn('#f1f3f6', C.gris), flex: 1 }}>Cancelar</button>
      <button onClick={() => onAgregar(tipo === 'flete' ? { es_flete: true, cantidad: cantidad.trim(), costo_unitario: costo.trim().replace(/,/g, ''), descripcion } : { tipo: producto.tipo, referencia_id: producto.referencia_id, cantidad: cantidad.trim(), costo_unitario: costo.trim().replace(/,/g, '') })} disabled={!listo} style={{ ...btn(C.verde), flex: 1, opacity: listo ? 1 : 0.5 }}>Agregar</button>
    </>}>
      {tipo !== 'flete' && !producto && <BuscarCatalogo onElegir={(p) => { setProducto(p); if (p.costo_catalogo != null && !costo) setCosto(String(p.costo_catalogo)); }} />}
      {tipo !== 'flete' && producto && <div style={{ ...card, padding: 10, fontSize: 12.5, fontWeight: 700, color: C.gris }}>{producto.nombre} <button onClick={() => setProducto(null)} style={{ ...btnMini(C.principal), marginLeft: 6 }}>cambiar</button></div>}
      {tipo === 'flete' && <><label style={labelStyle}>Descripción</label><input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} style={inputBase} /></>}
      {(tipo === 'flete' || producto) && (
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}><label style={labelStyle}>Cantidad</label><input inputMode="decimal" value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputBase} autoFocus={tipo !== 'flete'} /></div>
          <div style={{ flex: 1 }}><label style={labelStyle}>Costo unitario sin IVA (MXN)</label><input inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} style={inputBase} placeholder="0.00" /></div>
        </div>
      )}
    </Modal>
  );
}
